import { Entity, registerSystem, System } from "aframe";
import { Quaternion, Vector3, Vector3Like } from "three";
import { transformPoint, transformVector, untransformPoint } from "../three/math";
import { AmmoGlobal, AmmoBodyComponent, AmmoClosestRayResultCallback, AmmoPhysicsSystem, AmmoVector3, AmmoAllHitsRayResultCallback, AmmoCollisionObject } from "./ammo";
import { AmmoNormalComponent } from "./ammo-normal";
import { AvatarActionFactory, AvatarActionInstance, AvatarComponent, AvatarSystem, SetAnimationEventDetail } from "./avatar";
import { InputActionDetail, InputActionEvent } from "./input/common";

declare var Ammo : AmmoGlobal;



const teleport = (avatar : Entity, position : Vector3Like ) =>
{
	const scene = avatar.sceneEl!;
	const xr = scene.renderer.xr;
	if(xr && xr.enabled && xr.isPresenting)
	{
		//In VR, teleporting actually means moving the camera's parent.
		const cam = scene.camera.el.object3D;
		const origin = cam.parent!;
		const dx = cam.matrixWorld.elements[12] - origin.matrixWorld.elements[12];
		const dz = cam.matrixWorld.elements[14] - origin.matrixWorld.elements[14];
		origin.position.set( position.x - dx, position.y, position.z - dz );
		avatar.object3D.position.y = position.y; //Copy altitude only
		//untransformVector( origin.position.copy(cam.position), origin.matrix ).add(position);
	}
	else
		avatar.object3D.position.copy( position );
	
	const abc = avatar.components["ammo-body"] as AmmoBodyComponent;
	if(!abc) return;
	abc.moveTo(position, null);
	abc.body.getLinearVelocity().setValue(0,0,0);
};


const setEntityPos = (el : Entity, x : number, y : number, z : number) =>
{
	el.object3D.position.set(x, y, z);
	if(el.object3D.parent && (el.object3D.parent !== el.sceneEl!.object3D))
	{
		untransformPoint(el.object3D.position, el.object3D.parent!.matrixWorld);
	}
}

class AvatarTeleport implements AvatarActionInstance
{
	input : InputActionDetail;
	physics : AmmoPhysicsSystem;
	collisionFilterGroup : number;
	collisionFilterMask : number;
	teleportFilterGroup : number;
	teleportFilterMask : number;
	range : number;
	rayStart : AmmoVector3 = new Ammo.btVector3();
	rayEnd : AmmoVector3 = new Ammo.btVector3();
	widget : Entity;
	cursor? : Entity;
	target? : Entity;
	curve : number;

	delay : number;
	timeout : number;
	
	constructor(avatar : AvatarComponent, input : InputActionDetail, physicsSystem : AmmoPhysicsSystem, movementSystem : AvatarMovementSystem )
	{
		this.delay = 500;
		this.timeout = Date.now() + 500; //Can't teleport before aiming delay

		this.input = input;
		this.physics = physicsSystem;
		this.collisionFilterGroup = physicsSystem.getCollisionFilter(movementSystem.data.collisionGroup);
		this.collisionFilterMask = physicsSystem.getCollisionFilter(movementSystem.data.collisionMask);
		this.teleportFilterGroup = physicsSystem.getCollisionFilter(movementSystem.data.teleportGroup);
		this.teleportFilterMask = physicsSystem.getCollisionFilter(movementSystem.data.teleportMask);
		this.range = movementSystem.data.teleportRange;
		this.curve = movementSystem.data.teleportCurve;

		const clips : string[] = [ movementSystem.data.aimAnimation ];
		const detail : SetAnimationEventDetail = 
		{
			level : 1,
			animations : {
				"Idle" : { clips : clips },
				".*" : { filter : "Spine(\/|$)", clips : clips }
			}
		};

		(input.input || avatar).el.emit("set-animation", detail );

		const widget = movementSystem.data.teleportWidget;
		const cursor = movementSystem.data.teleportCursor;
		const target = movementSystem.data.teleportTarget;
		if(widget && cursor && target)
		{
			this.widget = widget;
			this.cursor = cursor;
			this.target = target;
			widget.object3D.visible = true;
			cursor.object3D.visible = true;
			target.object3D.visible = true;
		}
		else
		{
			this.widget = avatar.el.ownerDocument.createElement("a-entity") as Entity;
			this.widget.setAttribute("geometry", "primitive:cone; radiusBottom:0; radiusTop:0.25; height:0.5; position:0 0.25 0;");
			this.widget.setAttribute("material", "color:black; emissive:green; opacity:0.5; ");
			this.widget.setAttribute("geometry__2", "primitive:sphere; radius:0.1; ");
			this.widget.setAttribute("material__2", "color:black; emissive:green; ");
			avatar.el.sceneEl!.appendChild(this.widget);
		}
	}

	destroy(avatar : AvatarComponent)
	{
		if(this.cursor)
		{
			this.widget!.object3D.visible = false;
			this.cursor!.object3D.visible = false;
			this.target!.object3D.visible = false;
		}
		else
			avatar.el.sceneEl!.removeChild(this.widget);

		Ammo.destroy(this.rayStart);
		Ammo.destroy(this.rayEnd);

		const detail : SetAnimationEventDetail = 
		{
			level : 1,
			animations : {}
		};
		(this.input.input || avatar).el.emit("set-animation", detail );
	}

	tick(avatar : AvatarComponent, t: number, dt: number): boolean 
	{
		if(!avatar.groundContacts.length) return false;
		if(this.input.amount <= 0) //No more input
		{
			if(this.widget.object3D.visible) //Valid position : teleport!
				if(Date.now() >= this.timeout) //Aimed long enough
				{
					const targetPos = (this.target || this.widget).object3D.matrixWorld.elements;
					//teleport(avatar.el, (this.target || this.widget).object3D.position);
					teleport(avatar.el, { x:targetPos[12], y:targetPos[13], z:targetPos[14]});
				}
					
			return false;
		}

		const physicsWorld = this.physics.driver.physicsWorld;

		//const set = function(this : AmmoVector3, x : number, y : number, z : number) { this.setValue(x,y,z); };
		//this.input.getRay( set.bind(this.origin), set.bind(this.direction) );

		this.input.getRay( this.rayStart, this.rayEnd, Ammo.btVector3.prototype.setValue );
		
		if(this.input.input && (this.input.input.name == "xr-input"))
		{
			this.rayEnd.op_mul(1.0 / this.rayEnd.length());
			this.rayEnd.setY( this.rayEnd.y() - 0.2 );
		}
		this.rayEnd.op_mul(this.range / this.rayEnd.length());
		
		const dirX = this.rayEnd.x(), dirY = this.rayEnd.y(), dirZ = this.rayEnd.z();

		this.rayEnd.op_add(this.rayStart);

		//let result = new Ammo.ClosestRayResultCallback(this.rayStart, this.rayEnd);
		let result = new Ammo.AllHitsRayResultCallback(this.rayStart, this.rayEnd) as AmmoAllHitsRayResultCallback;
		result.m_collisionFilterGroup = this.collisionFilterGroup;
		result.m_collisionFilterMask = this.collisionFilterMask;
		
		physicsWorld.rayTest(this.rayStart, this.rayEnd, result);

		let closestHit = NaN;
		let hitNormal : AmmoVector3 | null = null, hitPoint : AmmoVector3 | null = null, hitObject : AmmoCollisionObject | null = null;
		for(let n = result.m_collisionObjects.size(); --n >= 0; )
		{
			const f = result.m_hitFractions.at(n);
			if(closestHit < f) continue;
			const co = result.m_collisionObjects.at(n);
			const ammoBc = this.physics.components[ Ammo.getPointer(co) ];
			const ammoEl = ammoBc && ammoBc.el;
			const ammoN = ammoEl && ammoEl.components[ "ammo-normal" + ammoBc.attrName!.slice(9) ] as AmmoNormalComponent;
			if(ammoN)
			{
				const N = new Vector3(ammoN.data.x, ammoN.data.y, ammoN.data.z);
				transformVector(N, ammoEl.object3D.matrixWorld);
				const dot = N.x * dirX + N.y * dirY + N.z * dirZ;
				if(dot > 0) continue; //Not facing ammo-normal
			}
			closestHit = f;
			hitObject = co;
			hitPoint = result.m_hitPointWorld.at(n);
			hitNormal = result.m_hitNormalWorld.at(n);
		}
		
		//if(( this.widget.object3D.visible = result.hasHit() ))
		//let hit : boolean = result.hasHit();
		if(hitObject)
		{
			if(hitNormal!.y() < 0.5) //Not hit a walkable surface
			{
				this.rayEnd.setValue( hitPoint!.x(), hitPoint!.y(), hitPoint!.z() );
				hitPoint = null;
			}
		}

		if( !hitPoint ) //Fall from ray end
		{
			this.rayStart.setValue( this.rayEnd.x(), this.rayEnd.y(), this.rayEnd.z() );
			this.rayEnd.setValue( this.rayStart.x(), this.rayStart.y() - this.range, this.rayStart.z() );

			// TODO ? : check normals too ?
			const fallResult : AmmoClosestRayResultCallback = new Ammo.ClosestRayResultCallback(this.rayStart, this.rayEnd);
			fallResult.m_collisionFilterGroup = this.collisionFilterGroup;
			fallResult.m_collisionFilterMask = this.collisionFilterMask;

			physicsWorld.rayTest(this.rayStart, this.rayEnd, fallResult);
			hitPoint = fallResult.hasHit() ? fallResult.m_hitPointWorld : null;
			hitObject = fallResult.m_collisionObject;
		}


		if( hitObject && (this.teleportFilterGroup || this.teleportFilterMask) ) //Check that hit object is a valid teleport target (not a wall)
		{
			const bh = hitObject.getBroadphaseHandle();
			if(this.teleportFilterGroup && !(this.teleportFilterGroup & bh.m_collisionFilterGroup))
				hitObject = null;
			else if(this.teleportFilterMask && !(this.teleportFilterMask & bh.m_collisionFilterMask))
				hitObject = null;
		}

		this.widget.object3D.visible = Boolean(hitObject);
		if(hitPoint)
		{
			const hitX = hitPoint.x(), hitY = hitPoint.y(), hitZ = hitPoint.z();

			setEntityPos(this.target || this.widget, hitX, hitY, hitZ )

			const avatarMat = avatar.el.object3D.matrixWorld.elements;
			const dx = hitX - avatarMat[12], dy = hitY - avatarMat[13], dz = hitZ - avatarMat[14];
			const angle = Math.atan2(dx, dz);

			const dt = this.timeout - Date.now();
			const kt = (dt > 0) ? dt / this.delay : 0.0; // 1 = at origin ; 0 = at target

			if(this.cursor)
			{
				const curveY = this.curve * 4.0 * kt * (1.0 - kt) * Math.sqrt(dx * dx + dz * dz);
				setEntityPos(this.cursor, hitX - kt * dx, hitY + curveY - kt * dy, hitZ - kt * dz);
			}
			else
			{
				this.widget.object3D.rotation.set(0, angle, 0, "YXZ");
				const movingMesh = this.widget.object3DMap.mesh__2;
				if(movingMesh)
					movingMesh.position.z = -kt * Math.sqrt(dx * dx + dz * dz);
			}

			if( avatar.velocity.lengthSq() < 0.1 )
			{
				avatar.orientation = angle;
			}
		}

		Ammo.destroy(result);

		return true;
	}
	
}




class AvatarTurn implements AvatarActionInstance
{
	constructor(avatar: AvatarComponent, angle : number)
	{
		const el = avatar.el;
		const scene = el.sceneEl!;
		const xr = scene.renderer.xr;
		if(xr && xr.enabled && xr.isPresenting)
		{
			const cam = scene.camera.el.object3D;
			const world = cam.parent!;
			const offset = new Quaternion(0, Math.sin(0.5*angle), 0, Math.cos(0.5*angle));
			cam.updateWorldMatrix(true, false);

			const dx = cam.matrixWorld.elements[12] - world.matrixWorld.elements[12], dz = cam.matrixWorld.elements[14] - world.matrixWorld.elements[14];
			const u = Math.cos(angle), v = Math.sin(angle);
			world.quaternion.premultiply( offset );
			world.position.x += dx - (u * dx + v * dz);
			world.position.z += dz - (u * dz - v * dx);
			cam.updateWorldMatrix(true, true);
		}
		else
			avatar.orientation += angle; //duh
	}
	tick(avatar: AvatarComponent): boolean { return false; }
	destroy(avatar: AvatarComponent): void {}
}



class AvatarMoveTo implements AvatarActionInstance
{
	targetEl? : Entity;
	targetPos? : Vector3;
	targetDir : Vector3;
	inputDetail : InputActionDetail;
	widget : Entity;
	inputListener : EventListener;
	constructor(avatar: AvatarComponent, input : InputActionDetail, targetEl : Entity, targetPos : Vector3)
	{
		const sceneEl = avatar.el.sceneEl!;

		this.inputListener = ((event : InputActionEvent) => 
		{
			if(event.detail.action.startsWith("Move"))
			{
				//Stop current action instance.
				delete this.targetEl;
				delete this.targetPos;
			}
		}) as EventListener;
		
		this.widget = sceneEl.ownerDocument.createElement("a-entity") as Entity;
		this.widget.setAttribute("geometry", "primitive:sphere; radius:0.1; ");
		this.widget.setAttribute("material", "color:#000; emissive:#00F; opacity:1;" );
		sceneEl.append(this.widget);

		this.targetEl = targetEl;
		this.targetPos = targetPos;
		this.targetDir = new Vector3();
		this.update(avatar); //Compute target dir.
		this.inputDetail = {
			action : "Move",
			timestamp : Date.now(),
			amount : 1.0,
			feedback : (i) => {},
			getRay : (o,d,set) => { 
				const avatarMat = avatar.el.object3D.matrixWorld.elements;
				set.call(o, avatarMat[12],avatarMat[13],avatarMat[14]); 
				set.call(d, this.targetDir.x, 0, this.targetDir.z); 
			}
		};
		avatar.el.emit("input-begin", this.inputDetail);

		avatar.el.addEventListener("input-begin", this.inputListener, { capture : true } );
	}
	update(avatar: AvatarComponent) : number
	{
		if(!(this.targetPos && this.targetEl)) return NaN;

		const avatarMat = avatar.el.object3D.matrixWorld.elements;
		const targetDir = this.targetDir;
		this.targetDir.copy(this.targetPos);
		transformPoint(targetDir, this.targetEl.object3D.matrixWorld);
		this.widget.object3D.position.copy(targetDir);
		targetDir.x -= avatarMat[12];
		targetDir.y = 0.0;
		targetDir.z -= avatarMat[14];
		return targetDir.lengthSq();
	}
	tick(avatar: AvatarComponent, t:number, dt:number, wasGrounded : boolean): boolean
	{
		return this.update(avatar) > 0.25;
	}
	destroy(avatar: AvatarComponent): void {
		avatar.el.removeEventListener("input-begin", this.inputListener, { capture : true } );
		avatar.el.emit("input-end", this.inputDetail);
		this.widget.remove();
	}
}

export interface AvatarMovementSystem extends System
{
	data : {
		collisionGroup : string[];
		collisionMask : string[];
		teleportGroup : string[];
		teleportMask : string[];
		teleportRange : number;
		moveRange : number;
		teleportWidget : Entity;
		teleportCursor : Entity;
		teleportTarget : Entity;
		teleportCurve : number;
		aimAnimation : string;
	};
	teleport( avatar : Entity, position : {x:number,y:number,z:number} ) : void;
}



const onceWithThreshold = ( factory : AvatarActionFactory ) : AvatarActionFactory =>
{
	let lastInput : InputActionDetail | null = null;
	return (avatar : AvatarComponent, input : InputActionDetail) =>
	{
		if( (input.amount >= 0.666) && (lastInput !== input) )
		{
			const result = factory(avatar, input);
			if(result && !input.instant) lastInput = input;
			return result;
		} 
		if( input.amount <= 0.333 ) lastInput = null;
		return null;
	};
};

registerSystem("avatar-movement", {
	schema : {
		collisionGroup : { type : "array" },
		collisionMask : { type : "array" },

		moveRange : { type : "number", default : 30.0 },

		teleportRange : { type : "number", default : 30.0 },
		teleportGroup : { type : "array" },
		teleportMask : { type : "array" },

		teleportWidget : { type : "selector" },
		teleportTarget : { type : "selector" },
		teleportCursor : { type : "selector" },
		teleportCurve : { type : "number" },

		aimAnimation : { type : "string", default : "Aim" }
	},
	init(this: AvatarMovementSystem )
	{
		
		
		
		const makeTurnAction = (angle : number) => onceWithThreshold((avatar : AvatarComponent, input : InputActionDetail) =>  new AvatarTurn(avatar, angle));

		const tpActions = {
			"Teleport" : onceWithThreshold( (avatar : AvatarComponent, input : InputActionDetail) =>
				{
					if(!avatar.groundContacts.length) return null;
					const physics = this.el.systems.physics as AmmoPhysicsSystem;
					return physics && new AvatarTeleport(avatar, input, physics, this );
				} ),
			"TurnLeft" : makeTurnAction(0.5 * Math.PI),
			"TurnRight" : makeTurnAction(-0.5 * Math.PI),
			"TurnAround" : makeTurnAction(Math.PI)
		}


		const avatarSystem = this.el.systems.avatar as AvatarSystem;
		const actions = avatarSystem.actions;
		for(let key in tpActions)
			actions[key] = (tpActions as any)[key];


		this.el.addEventListener("enable-teleport", (event) =>
		{
			const disabled = !(event as CustomEvent).detail;
			for(let key in tpActions)
				((tpActions as any)[key] as AvatarActionFactory).disabled = disabled;
		});

		actions["MoveTo"] = (avatar : AvatarComponent, input : InputActionDetail) =>
		{
			let hitEl : Entity | null = null;
			let hitPos : Vector3 | null = null;
			const physics = this.el.systems.physics as AmmoPhysicsSystem;
			const result : AmmoClosestRayResultCallback = new Ammo.ClosestRayResultCallback();
			input.getRay( result.m_rayFromWorld, result.m_rayToWorld, Ammo.btVector3.prototype.setValue );
			result.m_rayToWorld.op_mul(this.data.moveRange / result.m_rayToWorld.length()).op_add(result.m_rayFromWorld);
			result.m_collisionFilterGroup = physics.getCollisionFilter(this.data.collisionGroup);
			result.m_collisionFilterMask = physics.getCollisionFilter(this.data.collisionMask);
			physics.driver.physicsWorld.rayTest(result.m_rayFromWorld, result.m_rayToWorld, result);
			if(result.hasHit())
			{
				hitEl = physics.components[ Ammo.getPointer(result.m_collisionObject) ].el;
				hitPos = new Vector3( result.m_hitPointWorld.x(), result.m_hitPointWorld.y(), result.m_hitPointWorld.z() );
				untransformPoint(hitPos, hitEl.object3D.matrixWorld);
			}
			Ammo.destroy(result);
			return hitEl && new AvatarMoveTo(avatar, input, hitEl, hitPos!);
		};

		this.el.addEventListener("teleport-avatar", (event) =>
		{
			const whereEl = event.target as Entity;
			whereEl.object3D.updateWorldMatrix(true, false);

			const tpMat = whereEl.object3D.matrixWorld.elements;
			const tpPos = { x : tpMat[12], y : tpMat[13], z : tpMat[14] };
			
			teleport(avatarSystem.avatar, tpPos);
			avatarSystem.avatar.components.avatar.orientation = Math.atan2( -tpMat[2], tpMat[0] );
		});
	},
	update(this : AvatarMovementSystem)
	{
		this.data.teleportWidget && (this.data.teleportWidget.object3D.visible = false);
		this.data.teleportCursor && (this.data.teleportCursor.object3D.visible = false);
		this.data.teleportTarget && (this.data.teleportTarget.object3D.visible = false);
	},
	teleport(this:AvatarMovementSystem, avatar : Entity, position : Vector3Like ) : void
	{
		teleport(avatar, position);		
	}
});


